import React, { Component, Fragment } from 'react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import classNames from 'classnames'

import { COURSE_SIDE_NAV_CLASS_NAMES } from '@elo-kit/constants/customCss.constants'
import { COURSE_THEME_STYLE } from 'constants/themes.constants'
import { COMMUNITY_KEY } from 'constants/options.constants'

import { areUsernamesEqual } from 'utils/helpers.utils'

import CustomSideNav from './views/CustomSideNav'
import DefaultSideNav from './views/DefaultSideNav'

@observer
class SideNav extends Component {
  @observable collapsedMenus = new Map()
  @observable community = {}

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    const { sellersStore, username } = this.props

    const currentSeller = sellersStore.list.find((seller) => areUsernamesEqual(seller.username, username))
    const isCommunityAppActive = currentSeller && sellersStore.isAppActive(COMMUNITY_KEY, currentSeller.id)

    this.detectAndSetCollapsedMenus()

    if (isCommunityAppActive) {
      this.checkCommunity()
    }
  }

  componentDidUpdate() {
    const {
      courseViewsStore: { activeLesson },
    } = this.props
    if (activeLesson.id) {
      this.detectAndSetCollapsedMenus()
    }
  }

  @action setCommunity = (community) => (this.community = community)

  checkCommunity = async () => {
    const { communitiesStore, communityMemberStore, courseViewsStore } = this.props
    const { courseSession: { product } = {} } = courseViewsStore

    const resp = await communitiesStore.checkExistCommunity(product.id)
    const { data, success } = resp || {}
    const { list = [] } = data || {}

    if (success) {
      this.setCommunity(list[0])
      communityMemberStore.fetchItem(list[0]?.id, { skipErrorNotific: true })
    }
  }

  detectAndSetCollapsedMenus = () => {
    const {
      lessonsStore: { list },
    } = this.props
    list.forEach((lesson) => {
      if (lesson.isCategory) {
        this.collapsedMenus.set(
          lesson.id,
          this.checkIfCategoryContainActiveLesson(lesson.id) || this.isCategoryOpened(lesson.id)
        )
      }
    })
  }

  checkIfCategoryContainActiveLesson = (categoryId) => {
    const {
      courseViewsStore: { activeLesson = {} },
      lessonsStore: { list },
    } = this.props

    const lessonsInCategory = list.filter(({ parentId }) => parentId === categoryId)
    const containActiveLesson = lessonsInCategory.find(({ id }) => id === activeLesson.id)
    if (!containActiveLesson) {
      const categoriesInCategory = lessonsInCategory.filter(({ isCategory }) => !!isCategory)
      if (categoriesInCategory.length) {
        const reducer = (accumulator, category) => this.checkIfCategoryContainActiveLesson(category.id) || accumulator
        return categoriesInCategory.reduce(reducer, false)
      }
      return false
    }
    return true
  }

  findCurrentLesson = (currentLessonId, lessons = []) =>
    lessons.reduce((accumulator, currentValue) => {
      const lesson =
        currentValue.id === currentLessonId
          ? currentValue
          : this.findCurrentLesson(currentLessonId, currentValue.children || [])
      return accumulator || lesson
    }, null)

  isCategoryOpened = (categoryId) => {
    const {
      product: { courseMenuUnfold },
      courseThemeData: { prefs = {} },
      viewConfig: { layout },
      lessons,
    } = this.props.courseViewsStore
    const { lesson = {} } = prefs
    const themeUnfoldedMenus = lesson.unfoldedMenus
    const currentLesson = this.findCurrentLesson(categoryId, lessons)

    const { lessonSettings = {} } = currentLesson || {}

    const { unfoldedMenu } = lessonSettings
    const customView = layout === COURSE_THEME_STYLE.custom
    const courseMenuUnfolded = (customView ? themeUnfoldedMenus : courseMenuUnfold) || unfoldedMenu
    const menuCollapsed = this.collapsedMenus.get(categoryId)

    return (menuCollapsed === undefined && courseMenuUnfolded) || menuCollapsed
  }

  @action toggleMenu = (categoryId) => {
    this.collapsedMenus.set(categoryId, !this.isCategoryOpened(categoryId))
  }

  render() {
    const { courseViewsStore, defaultTheme, sideBarSide, communitiesStore, communityMemberStore } = this.props
    const {
      mobileSideMenuActive,
      toggleSideMobileMenu,
      viewConfig: { layout },
      mobileMenuActive,
    } = courseViewsStore

    const customView = layout === COURSE_THEME_STYLE.custom

    const mobileDefaultThemeActive = defaultTheme && mobileMenuActive

    const courseSideNavClasses = classNames('course-side-nav', COURSE_SIDE_NAV_CLASS_NAMES.containerClassName, {
      'course-side-nav--active': mobileSideMenuActive,
      'course-side-nav--active-default': mobileDefaultThemeActive,
    })

    const closeContainerClasses = classNames('course-side-nav__close', {
      'course-side-nav__close--active': mobileSideMenuActive || mobileDefaultThemeActive,
    })

    const containerClickHandler = defaultTheme ? () => {} : toggleSideMobileMenu

    return (
      <Fragment>
        {customView ? (
          <CustomSideNav
            collapsedMenus={this.collapsedMenus}
            isCategoryOpened={this.isCategoryOpened}
            toggleMenu={this.toggleMenu}
            sideBarSide={sideBarSide}
            containerClasses={courseSideNavClasses}
            community={this.community}
            handleCloseSidebar={containerClickHandler}
            courseViewsStore={courseViewsStore}
            communityMemberStore={communityMemberStore}
          />
        ) : (
          <div className={courseSideNavClasses}>
            <DefaultSideNav
              collapsedMenus={this.collapsedMenus}
              isCategoryOpened={this.isCategoryOpened}
              toggleMenu={this.toggleMenu}
              community={this.community}
              courseViewsStore={courseViewsStore}
              communitiesStore={communitiesStore}
              communityMemberStore={communityMemberStore}
            />
          </div>
        )}
        <div className={closeContainerClasses} onClick={containerClickHandler} />
      </Fragment>
    )
  }
}

SideNav.displayName = 'SideNav'

export default SideNav
